// Duplicate-client finder + merge UI. Opened from the "🔀 Duplicados"
// button on the Clients toolbar. Lists groups of same-name clients that
// also share a DOB / email / phone (server decides — GET
// /clients/duplicates), then lets the agent pick a survivor and merge a
// pair, resolving field conflicts one by one. Merging is pairwise: a
// 3-way group is collapsed by merging twice.

import { useEffect, useState } from 'react';
import { listClientDuplicates, mergeClients } from '../api.js';

// Scalar fields shown in the side-by-side compare. Keep in sync with the
// backend MERGE_FIELDS list (the server is the source of truth; this is
// just what we surface for conflict resolution). H5: DOB is excluded —
// duplicate detection already keys on DOB equality, and the survivor
// keeps its encrypted triple. If they truly differ the agent edits via
// the standard PIN-gated form after the merge.
const COMPARE_FIELDS = [
  ['email', 'Email'],
  ['phone', 'Teléfono'],
  ['city', 'Ciudad'],
  ['state', 'Estado'],
  ['status', 'Estado cliente'],
];

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
};

export function ClientDuplicates({ onClose, onMerged, lang }) {
  const t = (es, en) => (lang === 'es' ? es : en);
  const [groups, setGroups] = useState(null); // null = loading
  const [error, setError] = useState('');
  // Active merge: { groupIdx, primaryId, secondaryId, fields:{} }
  const [active, setActive] = useState(null);
  const [merging, setMerging] = useState(false);

  const load = () => {
    setGroups(null);
    setError('');
    listClientDuplicates()
      .then(setGroups)
      .catch((e) => { setError(e.message); setGroups([]); });
  };
  useEffect(load, []);

  const startMerge = (groupIdx, a, b) => {
    // Default survivor = the older record (first in the group, which the
    // backend already sorted by createdAt asc).
    setActive({ groupIdx, primaryId: a.id, secondaryId: b.id, fields: {} });
  };

  const doMerge = async () => {
    if (!active) return;
    setMerging(true);
    try {
      await mergeClients(active.primaryId, active.secondaryId, active.fields);
      setActive(null);
      if (typeof onMerged === 'function') onMerged();
      load(); // refresh the group list — the merged pair should be gone
    } catch (e) {
      setError(e.message);
    } finally {
      setMerging(false);
    }
  };

  // For the active pair, find which COMPARE_FIELDS differ (both non-empty
  // and not equal) — those need an explicit primary/secondary choice.
  const conflictRows = (() => {
    if (!active || !groups) return [];
    const grp = groups[active.groupIdx];
    if (!grp) return [];
    const p = grp.clients.find((c) => c.id === active.primaryId);
    const s = grp.clients.find((c) => c.id === active.secondaryId);
    if (!p || !s) return [];
    return COMPARE_FIELDS.map(([key, label]) => {
      const pv = p[key], sv = s[key];
      const empty = (x) => x === null || x === undefined || x === '';
      let kind = 'same';
      if (empty(pv) && !empty(sv)) kind = 'fill';        // auto from secondary
      else if (!empty(pv) && empty(sv)) kind = 'keep';   // primary only
      else if (!empty(pv) && !empty(sv) && fmtVal(pv) !== fmtVal(sv)) kind = 'conflict';
      return { key, label, pv, sv, kind };
    });
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, width: '100%', maxWidth: 880, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--c-border,#1a3550)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
            🔀 {t('Clientes duplicados', 'Duplicate clients')}
            {Array.isArray(groups) && <span style={{ marginLeft: 8, fontSize: '.72rem', color: 'var(--c-text3,#45688a)', fontWeight: 600 }}>
              ({groups.length} {t('grupos', 'groups')})
            </span>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', fontSize: '1.1rem' }}>✕</button>
        </div>

        <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '.6rem .85rem', color: 'var(--c-red,#ef4444)', fontSize: '.78rem', marginBottom: '.75rem' }}>
              ⚠ {error}
            </div>
          )}

          {groups === null && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>
              {t('Buscando duplicados…', 'Scanning for duplicates…')}
            </div>
          )}

          {Array.isArray(groups) && groups.length === 0 && !error && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)', fontSize: '.82rem' }}>
              ✅ {t('No se encontraron clientes duplicados.', 'No duplicate clients found.')}
            </div>
          )}

          {Array.isArray(groups) && groups.map((grp, gi) => (
            <div key={gi} style={{ border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, marginBottom: '.75rem', overflow: 'hidden' }}>
              <div style={{ background: 'var(--c-surface2,#112538)', padding: '.55rem .85rem', fontSize: '.8rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
                👥 {grp.name} <span style={{ color: 'var(--c-text3,#45688a)', fontWeight: 600 }}>· {grp.clients.length} {t('registros', 'records')}</span>
              </div>
              <div style={{ padding: '.5rem .85rem' }}>
                {grp.clients.map((cl, ci) => (
                  <div key={cl.id} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', padding: '.4rem 0', borderBottom: ci < grp.clients.length - 1 ? '1px solid var(--c-border,#1a3550)' : 'none', fontSize: '.74rem', color: 'var(--c-text,#c8dff0)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{cl.firstName} {cl.lastName}</div>
                      <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
                        {[cl.email, cl.phone, fmtVal(cl.dob)].filter((x) => x && x !== '—').join(' · ')}
                        {` · ${cl.policyCount}p ${cl.documentCount}d`}
                        {cl.hasSsn ? ' · 🔒SSN' : ''}
                        {` · ${t('creado', 'created')} ${fmtVal(cl.createdAt)}`}
                      </div>
                    </div>
                  </div>
                ))}
                {/* Pairwise merge controls — one button per adjacent pair. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem', marginTop: '.5rem' }}>
                  {grp.clients.slice(1).map((sec, idx) => (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => startMerge(gi, grp.clients[0], sec)}
                      style={{ fontSize: '.7rem', fontWeight: 700, padding: '.35rem .75rem', borderRadius: 7, border: '1px solid var(--c-cyan,#00d4ff)', background: 'rgba(0,212,255,.12)', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      {t('Combinar', 'Merge')}: {grp.clients[0].firstName} {grp.clients[0].lastName} ← #{idx + 2}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Merge resolution panel */}
        {active && groups && groups[active.groupIdx] && (() => {
          const grp = groups[active.groupIdx];
          const p = grp.clients.find((c) => c.id === active.primaryId);
          const s = grp.clients.find((c) => c.id === active.secondaryId);
          if (!p || !s) return null;
          return (
            <div style={{ borderTop: '2px solid var(--c-cyan,#00d4ff)', background: 'var(--c-surface2,#112538)', padding: '1rem 1.25rem', maxHeight: '46vh', overflowY: 'auto' }}>
              <div style={{ fontSize: '.82rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>
                {t('Combinar — elige qué registro sobrevive', 'Merge — choose which record survives')}
              </div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.75rem' }}>
                {[p, s].map((cand) => (
                  <button
                    key={cand.id}
                    type="button"
                    onClick={() => setActive((a) => ({ ...a, primaryId: cand.id, secondaryId: cand.id === p.id ? s.id : p.id, fields: {} }))}
                    style={{ flex: 1, textAlign: 'left', padding: '.5rem .7rem', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                      border: `2px solid ${active.primaryId === cand.id ? 'var(--c-green,#00ff88)' : 'var(--c-border,#1a3550)'}`,
                      background: active.primaryId === cand.id ? 'rgba(0,255,136,.1)' : 'var(--c-surface,#0d1c2e)' }}
                  >
                    <div style={{ fontSize: '.7rem', fontWeight: 800, color: active.primaryId === cand.id ? 'var(--c-green,#00ff88)' : 'var(--c-text2,#7aa3c0)' }}>
                      {active.primaryId === cand.id ? `★ ${t('SOBREVIVE', 'SURVIVES')}` : t('se absorbe', 'absorbed')}
                    </div>
                    <div style={{ fontSize: '.74rem', color: 'var(--c-text,#c8dff0)', marginTop: 2 }}>
                      {cand.firstName} {cand.lastName} · {cand.policyCount}p · {fmtVal(cand.createdAt)}
                    </div>
                  </button>
                ))}
              </div>

              <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
                {t('Campos vacíos se rellenan automáticamente. Solo elige en los conflictos:', 'Empty fields auto-fill. Pick only on conflicts:')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                {conflictRows.map((row) => (
                  <div key={row.key} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: '.4rem', alignItems: 'center', fontSize: '.72rem', padding: '.25rem 0', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
                    <div style={{ color: 'var(--c-text3,#45688a)', fontWeight: 600 }}>{row.label}</div>
                    {row.kind === 'conflict' ? (
                      <>
                        <button type="button"
                          onClick={() => setActive((a) => ({ ...a, fields: { ...a.fields, [row.key]: 'primary' } }))}
                          style={{ textAlign: 'left', padding: '.3rem .5rem', borderRadius: 6, fontFamily: 'inherit', fontSize: '.7rem', cursor: 'pointer',
                            border: `1px solid ${(active.fields[row.key] || 'primary') === 'primary' ? 'var(--c-green,#00ff88)' : 'var(--c-border,#1a3550)'}`,
                            background: (active.fields[row.key] || 'primary') === 'primary' ? 'rgba(0,255,136,.1)' : 'transparent',
                            color: 'var(--c-text,#c8dff0)' }}>
                          {fmtVal(row.pv)} <span style={{ color: 'var(--c-text3,#45688a)' }}>({t('sobrevive', 'survivor')})</span>
                        </button>
                        <button type="button"
                          onClick={() => setActive((a) => ({ ...a, fields: { ...a.fields, [row.key]: 'secondary' } }))}
                          style={{ textAlign: 'left', padding: '.3rem .5rem', borderRadius: 6, fontFamily: 'inherit', fontSize: '.7rem', cursor: 'pointer',
                            border: `1px solid ${active.fields[row.key] === 'secondary' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
                            background: active.fields[row.key] === 'secondary' ? 'rgba(0,212,255,.12)' : 'transparent',
                            color: 'var(--c-text,#c8dff0)' }}>
                          {fmtVal(row.sv)} <span style={{ color: 'var(--c-text3,#45688a)' }}>({t('absorbido', 'absorbed')})</span>
                        </button>
                      </>
                    ) : (
                      <div style={{ gridColumn: '2 / 4', color: row.kind === 'fill' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text,#c8dff0)' }}>
                        {row.kind === 'fill'
                          ? `${fmtVal(row.sv)} ${t('(se rellena)', '(auto-fill)')}`
                          : fmtVal(row.pv)}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '.5rem', marginTop: '.85rem' }}>
                <button type="button" onClick={() => setActive(null)} disabled={merging}
                  style={{ flex: 1, padding: '.55rem', borderRadius: 8, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface,#0d1c2e)', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                  {t('Cancelar', 'Cancel')}
                </button>
                <button type="button" onClick={doMerge} disabled={merging}
                  style={{ flex: 2, padding: '.55rem', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#0099cc,#00d4ff)', color: '#06121f', fontWeight: 800, fontSize: '.8rem', cursor: merging ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: merging ? 0.6 : 1 }}>
                  {merging ? t('Combinando…', 'Merging…') : t('Confirmar combinación', 'Confirm merge')}
                </button>
              </div>
              <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', marginTop: '.5rem' }}>
                {t('Pólizas, documentos, notas, tareas, etc. del registro absorbido se mueven al que sobrevive. El absorbido se archiva (recuperable en base de datos).',
                   'Policies, documents, notes, tasks, etc. from the absorbed record move to the survivor. The absorbed one is archived (recoverable in the DB).')}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
